import { Play } from "lucide-react"
import { useCallback, useEffect, useMemo, useRef, useState } from "react"

import type { BattleInfo } from "../shared-sortaballs/PuzzleHeader"
import type { GameState } from "./logic"
import type {
  SortaballsPuzzle,
  SortaballsVariantConfig,
} from "./sortaballs-variant"

import { fireSuccessFeedbackCanvas } from "../modules/feedback-canvas"
import {
  clearCurrentPuzzle,
  loadCurrentPuzzleBoard,
} from "../shared-sortaballs/current-puzzle-storage"
import {
  GameplayTourView,
  useGameplayTour,
} from "../shared-sortaballs/gameplay-tour"
import { PuzzleHeader } from "../shared-sortaballs/PuzzleHeader"
import { useCurrentPuzzleBoardPersist } from "../shared-sortaballs/use-current-puzzle-board-persist"
import { advanceBoard } from "../sortaballs-challenges/advance-board"
import { Board } from "./Board"
import { isSolved } from "./logic"
import { useIncrementalReplay } from "./use-incremental-replay"
import { useSortaballs } from "./use-sortaballs"
import "../sortaballs-8-colors/puzzle-ui.css"
import "./sortaballs.css"

interface SortaballsGameProps {
  advancedBy?: number
  battle?: BattleInfo
  config: SortaballsVariantConfig
  hideHints?: boolean
}

// One game shell for the whole sortaballs family. A variant renders it with
// its SortaballsVariantConfig; everything below is the shared implementation
// that used to be copy-pasted across 7 Apps. The shell itself only resolves
// the puzzle id and (when the variant's set loads lazily) warms the chunk —
// the board-heavy subtree lives in SortaballsGameBoard, which mounts only once
// a real puzzle exists, so the engine never sees an undefined board.
export function SortaballsGame({
  advancedBy = 0,
  battle,
  config,
  hideHints = false,
}: SortaballsGameProps) {
  // Current puzzle comes from the shared level store, so the sidebar panel
  // and the game page stay in sync (persisted to localStorage by the store).
  const currentPuzzleId = config.levelStore.useLevel()

  const currentPuzzle = config.puzzles.getPuzzleById(currentPuzzleId) ?? null

  // Lazily-loading variants (10-colors): the puzzle set may still be its own
  // chunk. Until it lands, currentPuzzle is null and the shell shows the
  // title fallback.
  const puzzlesReady = config.puzzles.useReady?.() ?? true

  // Kick the puzzle chunk load on mount, and once it lands resync the stored
  // level — an id seeded before the data arrived fell back to puzzle 1 during
  // the first render and must be corrected now.
  useEffect(()=> {
    if (!puzzlesReady) {
      config.puzzles.warm?.()
      return
    }
    config.levelStore.resyncFromStorage()
  }, [config.levelStore, config.puzzles, puzzlesReady])

  if (!puzzlesReady || !currentPuzzle) {
    return (
      <div className="sortaballs flex min-h-dvh flex-col items-center justify-center bg-primary-background p-8">
        <h1 className="mb-6 text-3xl font-bold text-foreground">{config.title}</h1>
      </div>
    )
  }

  return (
    <SortaballsGameBoard
      advancedBy={advancedBy}
      battle={battle}
      config={config}
      currentPuzzle={currentPuzzle}
      currentPuzzleId={currentPuzzleId}
      hideHints={hideHints}
    />
  )
}

function SortaballsGameBoard(input: {
  advancedBy: number
  battle?: BattleInfo
  config: SortaballsVariantConfig
  currentPuzzle: SortaballsPuzzle
  currentPuzzleId: number
  hideHints: boolean
}) {
  const { advancedBy, battle, config, currentPuzzle, currentPuzzleId, hideHints } = input

  // Accepted-challenge opening: when the challenge starts with the creator's
  // hint moves, the acceptor should SEE those moves play out, not jump ahead.
  // While `isAnimatingOpen` is true the engine runs from the ORIGINAL board;
  // once the pre-roll finishes the initial flips to the advanced board so a
  // later Reset goes straight to the advanced start (the animation never
  // re-runs — it is guarded by `preRolledRef` and only fires on this mount).
  const [isAnimatingOpen, setIsAnimatingOpen] = useState(
    config.advanced ? advancedBy > 0 : false,
  )
  const preRolledRef = useRef(false)

  // Restored session for the current puzzle (null until one was saved). The
  // saved move list lets a reload REPLAY the session — the engine rebuilds
  // history and undo from it instead of seeding a bare board.
  const restored = useMemo(()=> {
    if (battle || isAnimatingOpen) return null
    return loadCurrentPuzzleBoard(currentPuzzle.boardId)
  }, [battle, isAnimatingOpen, currentPuzzle])

  // The engine always starts from the puzzle's OWN initial board; a restored
  // session replays its saved moves on top (via initialMoves below), so a
  // user reset returns to this board and undo walks the replayed history.
  const initialState: GameState | undefined = useMemo(()=> {
    if (isAnimatingOpen) return currentPuzzle.state
    return advanceBoard(
      currentPuzzle.state,
      currentPuzzle.correctMoves ?? [],
      advancedBy,
    )
  }, [currentPuzzle, advancedBy, isAnimatingOpen])

  const {
    board,
    clearMovingBall,
    clearSelection,
    commitSelectPlate,
    demoSelectPlate,
    flightKey,
    groupSize,
    moveCount,
    moves,
    movingBall,
    reset,
    selectAll,
    selectedPlate,
    selectionCount,
    selectPlate,
    undo,
  } = useSortaballs(
    initialState,
    config.capacities ?? config.capacity,
    restored?.moves,
  )

  const {
    canReveal,
    isPlaying,
    nextCount,
    revealedCount,
    revealNext,
    startReplay,
  } = useIncrementalReplay({
    clearSelection,
    commitSelectPlate,
    committedMoves: moves,
    demoSelectPlate,
    moves: currentPuzzle?.correctMoves ?? [],
    reset,
  })

  // Accepted-challenge opening: programmatically trigger the SAME logic as
  // pressing the (hidden) hint button `hints_used` times — the from-scratch
  // reveal machinery replays the first `advancedBy` correct moves with the
  // normal flight animation, so the acceptor SEES the creator's hints play
  // out. While it runs, `isPlaying` blocks all user input (plate clicks and
  // header buttons are ignored). Runs ONCE on mount; afterwards `reset`
  // targets the advanced board, so it can never re-run.
  const preRollRunningRef = useRef(false)
  const preRollSeenPlayingRef = useRef(false)

  useEffect(()=> {
    if (!config.advanced || advancedBy <= 0) {
      setIsAnimatingOpen(false)
      return
    }
    if (preRolledRef.current) return
    preRolledRef.current = true
    if ((currentPuzzle?.correctMoves ?? []).length === 0) {
      setIsAnimatingOpen(false)
      return
    }
    preRollRunningRef.current = true
    startReplay({ count: advancedBy })
  }, [advancedBy, config.advanced, currentPuzzle, startReplay])

  // When the reveal runs its course (isPlaying observed true, then false),
  // flip the reset target to the advanced board the acceptor starts from.
  // The "observed true" guard matters: the trigger effect above starts the
  // replay in the same commit this effect reads `isPlaying`, so without it
  // the flip would happen instantly and skip the animation.
  useEffect(()=> {
    if (!config.advanced || !preRollRunningRef.current) return
    if (isPlaying) {
      preRollSeenPlayingRef.current = true
      return
    }
    if (preRollSeenPlayingRef.current) {
      preRollRunningRef.current = false
      preRollSeenPlayingRef.current = false
      setIsAnimatingOpen(false)
    }
  }, [config.advanced, isPlaying])

  const solved = useMemo(()=> isSolved(board), [board])

  // Near-win marker — computed ONLY once the move has settled (no ball in
  // flight) and from NEAR_WIN_MIN_MOVES moves on. The depth-3 search
  // (isSolvedWithinMoves) is O(validMoves^3) with JSON.stringify memo keys, so
  // running it on the move commit spikes the main thread exactly as the flight
  // starts; deferring to `movingBall === null` plus the move-count floor keeps
  // it off the animation path (it only ever matters in the endgame anyway).
  const NEAR_WIN_ENABLED = true
  const NEAR_WIN_MIN_MOVES = 35
  const nearWinEligible =
    NEAR_WIN_ENABLED && moveCount >= NEAR_WIN_MIN_MOVES && movingBall === null
  const nearSolved = useMemo(
    ()=> (nearWinEligible ? (config.nearWin?.(board) ?? false) : false),
    [board, config.nearWin, nearWinEligible],
  )
  const successReached = solved || nearSolved

  useEffect(()=> {
    if (successReached && currentPuzzle && moveCount > 0) {
      config.progress.saveProgress(currentPuzzle.boardId, moveCount)
    }
  }, [config, currentPuzzle, moveCount, successReached])

  // Celebrate on the near-win or the real solve: confetti + a haptic pulse on
  // mobile. Fires once — `successReached` flips true a move early and stays
  // true through the final move, so the deps are stable afterwards.
  useEffect(()=> {
    if (successReached && moveCount > 0) {
      fireSuccessFeedbackCanvas(undefined, true)
    }
  }, [successReached, moveCount])

  // Persist the in-progress board (and the committed moves that make restore
  // replayable) once the player has made their first move, so a reload (or
  // returning to this puzzle) resumes where they left off. Debounced — rapid
  // moves coalesce into one write instead of re-serializing the whole session
  // on every commit. Skipped during challenge/battle and once a win is reached.
  useCurrentPuzzleBoardPersist({
    board,
    boardId: currentPuzzle.boardId,
    moves,
    paused: !!battle || successReached,
  })

  // A win clears the saved board — replaying the puzzle starts from scratch.
  useEffect(()=> {
    if (successReached) clearCurrentPuzzle()
  }, [successReached])

  const handlePreviousPuzzle = ()=> {
    const prev = config.puzzles.getPuzzleById(currentPuzzleId - 1)
    if (prev) config.levelStore.setLevel(prev.id)
  }

  const handleNextPuzzle = ()=> {
    const next = config.puzzles.getPuzzleById(currentPuzzleId + 1)
    if (next) config.levelStore.setLevel(next.id)
  }

  const handleUndo = ()=> {
    if (isPlaying) return
    undo()
  }

  const handleReset = ()=> {
    reset()
    // A user reset abandons the in-progress session: clear it so a later
    // reload starts from the original board instead of resurrecting it.
    clearCurrentPuzzle()
  }

  const canGoPrevious =
    config.puzzles.getPuzzleById(currentPuzzleId - 1) !== undefined
  const canGoNext =
    config.puzzles.getPuzzleById(currentPuzzleId + 1) !== undefined

  // Auto-playing guided tour — drives real selections/moves on this board.
  // Off in battle (challenges) mode; otherwise reuses the sidebar Start Tour
  // trigger and the shared dismissal flag.
  const tour = useGameplayTour({
    board,
    capacity: config.capacities ?? config.capacity,
    clearSelection,
    demoHelp: revealNext,
    demoReset: handleReset,
    demoSelectPlate,
    demoUndo: handleUndo,
    disabled: !!battle,
    isRevealPlaying: isPlaying,
    moveCount,
    selectAll,
  })

  // Base shows the revealed count so far; the color variants show the next
  // target count (5 on the first press).
  const helpMoveCount =
    config.helpMoveCount === "revealed" ? revealedCount : nextCount

  // Stable plate handlers: while a reveal plays (or the board is won) taps are
  // dropped; otherwise they route to the engine's selectors. `useCallback`
  // keeps the identities stable across renders so memoized Plate/Board skip
  // re-renders for unrelated state changes.
  const handlePlateClick = useCallback(
    (index: number)=> {
      if (isPlaying || successReached) return
      selectPlate(index)
    },
    [isPlaying, selectPlate, successReached],
  )
  const handlePlateLongPress = useCallback(
    (index: number)=> {
      if (isPlaying || successReached) return
      selectAll(index)
    },
    [isPlaying, selectAll, successReached],
  )

  // E2E hook (base + 3-colors): the root carries `data-success-reached`
  // (solved OR one move from solved) so e2e can observe the near-win trigger
  // without a visible block — the confetti burst is the only success feedback.
  // The board is shared by both layouts: normal play embeds it in the header's
  // centre rail, battle keeps the flat top header with the board beneath.
  const boardElement = (
    <Board
      board={board}
      capacity={config.capacity}
      caps={config.capacities}
      flightKey={flightKey}
      groupSize={groupSize}
      holdCueIndex={tour.holdCueIndex}
      movingBall={movingBall}
      onMovingBallDone={clearMovingBall}
      onPlateClick={handlePlateClick}
      onPlateLongPress={handlePlateLongPress}
      selectedPlate={selectedPlate}
      selectionCount={selectionCount}
      sizing={config.sizing}
      platesPerRow={config.platesPerRow}
    />
  )

  return (
    <div
      className="
        sortaballs flex min-h-dvh flex-col items-center justify-center bg-primary-background p-8
      "
      data-success-reached={config.nearWin ? successReached : undefined}
    >
      <PuzzleHeader
        battle={battle}
        canHelp={canReveal}
        center={battle ? undefined : boardElement}
        checkIfSolved={config.progress.checkIfSolved}
        disabled={isPlaying || !!battle?.paused}
        hasNext={canGoNext}
        hasPrevious={canGoPrevious}
        helpMoveCount={helpMoveCount}
        hideHelp={hideHints}
        highlightButton={tour.highlightButton}
        moveCount={moveCount}
        onHelp={revealNext}
        onNext={handleNextPuzzle}
        onPrevious={handlePreviousPuzzle}
        onReset={handleReset}
        onUndo={handleUndo}
        puzzle={currentPuzzle}
        solved={successReached && moveCount > 0}
        totalPuzzles={config.puzzles.getTotalPuzzleCount()}
      />

      {/* Battle mode keeps the flat top header, so the board goes underneath it
          rather than inside the header's centre. */}
      {battle && boardElement}

      {config.advanced && battle?.paused && (
        <div
          aria-label="Resume game"
          className="battle-pause-overlay"
          onClick={()=> battle.onTogglePause?.()}
          onKeyDown={event=> {
            if (event.key === "Enter" || event.key === " ") {
              battle.onTogglePause?.()
            }
          }}
          role="button"
          tabIndex={0}
        >
          <Play size={56} />
        </div>
      )}

      <GameplayTourView
        canAdvance={tour.canAdvance}
        moveCount={tour.moveCount}
        onCancelStart={tour.cancelStart}
        onClose={tour.close}
        onConfirmStart={tour.confirmStart}
        onContinue={tour.continue}
        pendingStartConfirm={tour.pendingStartConfirm}
        stepText={tour.stepText}
      />
    </div>
  )
}